import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import { Button, Chip, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { ModalCreateStatement } from '~/components/Dialog';
import { ReportDownloadMenu } from '~/components/ReportDownloadMenu';
import { ReportingTable } from '~/components/ReportingTable';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { ReportingSettlementReport } from '~/typings/API';

const Info = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 4px;
  max-width: 100%;
  padding-right: 8px;
`;

const InfoInner = styled.div`
  max-width: 100%;
  display: flex;
  align-items: center;
  line-height: 24px;
  gap: 4px;
`;

const Arrow = styled(Icon.ArrowRight)`
  display: inline-block;
  vertical-align: top;

  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const PageStatements: FC = () => {
  const [isCreateStatementOpen, setIsCreateStatementOpen] = useState(false);
  const openModal = () => setIsCreateStatementOpen(true);
  const closeModal = () => setIsCreateStatementOpen(false);
  const navigate = useNavigate();
  const enableCustomCSVStatements = useFeatureFlag(FeatureFlag.EnableCustomCSVStatements);

  const overwriteData = useCallback(
    (entry: ReportingSettlementReport, object: any) => ({
      ...object,
      ...(entry.statementSubjectDescription && {
        information: (
          <Info>
            <InfoInner>
              {entry.fromDate} <Arrow /> {entry.toDate}
            </InfoInner>
            <Chip type="info">{entry.statementSubjectDescription}</Chip>
          </Info>
        ),
      }),
      action: <ReportDownloadMenu report={entry} size="xs" variant="secondary" />,
    }),
    []
  );

  return (
    <>
      <ReportingTable
        tableId="statements"
        filter={{ category: 'statement' }}
        empty={{
          icon: <Icon.AnimationDocument />,
          title: 'No statements found',
        }}
        onRowClick={(entry) => navigate(`${ROUTE.STATEMENTS}/${entry.id}`)}
        overwriteEntryObject={overwriteData}
        action={
          enableCustomCSVStatements && (
            <Button
              icon={<Icon.AnimationDownload />}
              size="small"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                openModal();
              }}
            >
              Export Custom CSV
            </Button>
          )
        }
      />
      <ModalCreateStatement isOpen={isCreateStatementOpen} onClose={closeModal} />
    </>
  );
};
